import React from 'react';
import useClockTime from '../useClockTime';

const CX = 150, CY = 150;
const TICK_OUTER = 124;
const NUMERAL_R = 107;

const ticks = Array.from({ length: 60 }, (_, i) => {
  const angle = (i * 6 - 90) * Math.PI / 180;
  const isHour = i % 5 === 0;
  const innerR = isHour ? TICK_OUTER - 14 : TICK_OUTER - 7;
  return {
    x1: CX + TICK_OUTER * Math.cos(angle),
    y1: CY + TICK_OUTER * Math.sin(angle),
    x2: CX + innerR * Math.cos(angle),
    y2: CY + innerR * Math.sin(angle),
    strokeWidth: isHour ? 2.5 : 1,
  };
});

const numerals = Array.from({ length: 12 }, (_, i) => {
  const num = i + 1;
  const angle = (num * 30 - 90) * Math.PI / 180;
  return { num, x: CX + NUMERAL_R * Math.cos(angle), y: CY + NUMERAL_R * Math.sin(angle) };
});

const GoldenSynagogue = () => {
  const { hourDeg, minuteDeg, secondDeg } = useClockTime();

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 300, height: 300 }}>
      <svg width={300} height={300} viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="gs-face" cx="40%" cy="35%">
            <stop offset="0%" stopColor="#1e3a5f" />
            <stop offset="100%" stopColor="#0d1b3a" />
          </radialGradient>
          <radialGradient id="gs-outer" cx="50%" cy="50%">
            <stop offset="70%" stopColor="#0d1b3a" />
            <stop offset="100%" stopColor="#1a2f4e" />
          </radialGradient>
        </defs>

        {/* Outer background */}
        <circle cx={CX} cy={CY} r="148" fill="url(#gs-outer)" />

        {/* Gold frame ring */}
        <circle cx={CX} cy={CY} r="148" fill="none" stroke="#c8a84b" strokeWidth="11" />
        <circle cx={CX} cy={CY} r="142" fill="none" stroke="#f0d88a" strokeWidth="0.8" />
        <circle cx={CX} cy={CY} r="137" fill="none" stroke="#9a7a2c" strokeWidth="0.8" />

        {/* Decorative dots on outer frame */}
        {Array.from({ length: 36 }, (_, i) => {
          const angle = (i * 10 - 90) * Math.PI / 180;
          const r = 143;
          return (
            <circle key={i}
              cx={CX + r * Math.cos(angle)}
              cy={CY + r * Math.sin(angle)}
              r={i % 3 === 0 ? 3 : 1.8}
              fill={i % 3 === 0 ? '#f0d88a' : '#c8a84b'}
            />
          );
        })}

        {/* Diamond ornaments at 12/3/6/9 on outer frame */}
        {[0, 90, 180, 270].map(deg => {
          const angle = (deg - 90) * Math.PI / 180;
          const r = 143;
          const cx2 = CX + r * Math.cos(angle);
          const cy2 = CY + r * Math.sin(angle);
          return (
            <polygon key={deg}
              points={`${cx2},${cy2 - 5} ${cx2 + 3.5},${cy2} ${cx2},${cy2 + 5} ${cx2 - 3.5},${cy2}`}
              fill="#f0d88a"
              transform={`rotate(${deg}, ${cx2}, ${cy2})`}
            />
          );
        })}

        {/* Clock face */}
        <circle cx={CX} cy={CY} r="132" fill="url(#gs-face)" />
        <circle cx={CX} cy={CY} r="132" fill="none" stroke="#c8a84b" strokeWidth="2" />
        <circle cx={CX} cy={CY} r="127" fill="none" stroke="#c8a84b" strokeWidth="0.6" />

        {/* Subtle inner glow ring */}
        <circle cx={CX} cy={CY} r="120" fill="none" stroke="#c8a84b" strokeWidth="0.3" opacity="0.5" />

        {/* Tick marks */}
        {ticks.map((t, i) => (
          <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke="#c8a84b" strokeWidth={t.strokeWidth} strokeLinecap="round" />
        ))}

        {/* Numerals */}
        {numerals.map(({ num, x, y }) => (
          <text key={num} x={x} y={y + 4.5} textAnchor="middle"
            fontSize="13.5" fontFamily="Georgia, 'Times New Roman', serif"
            fill="#c8a84b" fontWeight="400" letterSpacing="0.5">
            {num}
          </text>
        ))}

        {/* Hour hand */}
        <g transform={`rotate(${hourDeg}, ${CX}, ${CY})`}>
          <line x1={CX} y1={CY + 18} x2={CX} y2={CY - 72}
            stroke="#e8d5a3" strokeWidth="5.5" strokeLinecap="round" />
        </g>

        {/* Minute hand */}
        <g transform={`rotate(${minuteDeg}, ${CX}, ${CY})`}>
          <line x1={CX} y1={CY + 20} x2={CX} y2={CY - 96}
            stroke="#f0ead0" strokeWidth="3.5" strokeLinecap="round" />
        </g>

        {/* Second hand */}
        <g transform={`rotate(${secondDeg}, ${CX}, ${CY})`}>
          <line x1={CX} y1={CY + 24} x2={CX} y2={CY - 104}
            stroke="#e53935" strokeWidth="1.5" strokeLinecap="round" />
        </g>

        {/* Center cap */}
        <circle cx={CX} cy={CY} r="6" fill="#c8a84b" />
        <circle cx={CX} cy={CY} r="3" fill="#0d1b3a" />
      </svg>
    </div>
  );
};

export default GoldenSynagogue;
